import { makeAutoObservable, reaction } from 'mobx'
import { nanoid } from 'nanoid'
import { toastStore } from 'shared/ui'
import { type IUser } from 'entities/Users'
import { ConversationAssignModalStore } from 'pages/conversations/ui/ConversationAssignModal'
import { type ConversationListStore } from 'pages/conversations/ui/ConversationList'

export class ConversationAssignDropdownStore {
  private _search = ''
  private _conversationId: number | null = null
  private _activeUserId: number | null = null
  private _itemsMap: Map<number, IUser> = new Map()
  private _isBulk = false
  private readonly _conversationAssignModalStore: ConversationAssignModalStore

  constructor(private _conversationListStore: ConversationListStore) {
    makeAutoObservable(this)

    this._conversationAssignModalStore = new ConversationAssignModalStore(
      this._conversationListStore.filtersStore,
      this._conversationListStore.entities
    )
    this.reactionConversationId()
    this._conversationAssignModalStore.setConfig({
      conversationListStore: this._conversationListStore,
    })
  }

  get entities() {
    return this._conversationListStore.entities
  }

  get conversationStore() {
    return this._conversationListStore.conversationStore
  }

  get isSearchEmpty() {
    return this._search === ''
  }

  get list() {
    return Array.from(this._itemsMap.values())
  }

  get isEmptyFilteredList() {
    return this.filteredListCount === 0
  }

  get currentConversation() {
    if (!this._conversationId) return null

    const conversation = this.conversationStore.getItem(this._conversationId)
    if (!conversation) return null

    return conversation
  }

  get filteredListCount() {
    return this.filteredList.length
  }

  get currentUserId() {
    return this.entities.usersStore.user?.id ?? null
  }

  get canReassignConversations() {
    return this.entities.userPermissionsStore.canReassignConversations
  }

  get filteredList() {
    const baseList = this.canReassignConversations
      ? this.list
      : this.currentUserId
      ? this.list.filter((member) => member.id === this.currentUserId)
      : []

    if (this.isSearchEmpty) {
      return baseList
    }

    return baseList.filter((member) =>
      member.name.toLowerCase().includes(this._search.toLowerCase())
    )
  }

  get selectedHasAssignedConversation() {
    return this._conversationListStore.selectedList.some((conversation) =>
      Boolean(conversation.user_id)
    )
  }

  get selectedAllAssigned() {
    const selected = this._conversationListStore.selectedList
    return selected.length > 0 && selected.every((conversation) => Boolean(conversation.user_id))
  }

  onSearch = (value: string) => {
    this._search = value
  }

  onReassign = async (id: number | null) => {
    if (id === this._activeUserId || !this._conversationId) return
    await this.conversationStore.reassign(this._conversationId, id)
    this.refresh(this._conversationId)
  }

  onReassignBulk = async (ids: number[]) => {
    if (!this.currentConversation) return

    const conversationIds = this.canReassignConversations
      ? this._conversationListStore.selectedListIds
      : this._conversationListStore.selectedList
          .filter((conversation) => !conversation.user_id)
          .map((conversation) => conversation.id)

    if (conversationIds.length) {
      await this.conversationStore.reassignBulk(
        conversationIds,
        ids,
        this.currentConversation.inbox_id,
        this.canReassignConversations ? this._conversationListStore.isSelectedAll : false,
        this._conversationListStore.filtersStore.filter?.key
      )
    }
    this._conversationListStore.handleResetSelect()
    if (this._conversationId) this.refresh(this._conversationId)
  }

  reset = () => {
    this._search = ''
    this._activeUserId = null
    this._itemsMap.clear()
    this._conversationId = null
  }

  refresh = (value: number) => {
    this._search = ''
    this._activeUserId = null
    this._itemsMap.clear()

    const conversation = this.conversationStore.getItem(value)
    if (!conversation) return null

    this._activeUserId = conversation.user_id

    const inbox = this.entities.inboxesStore.getItem(conversation.inbox_id)
    if (!inbox) return null

    if (inbox.type === 'inbox') {
      inbox.memberIds.forEach((id) => {
        const member = this.entities.usersStore.getItem(id)
        if (!member || member.isViewOnlyRole) return
        this._itemsMap.set(id, member)
      })
    }
  }

  setConversationId = (value: number) => {
    this._conversationId = value
  }

  setIsBulk = (value: boolean) => {
    this._isBulk = value
  }

  handleUpdateAssign = async (id: number | null) => {
    const member = this.entities.usersStore.getItem(id)
    const nanoId = nanoid()

    const assignTimeoutId = setTimeout(async () => {
      if (this._isBulk) {
        this.onReassignBulk(id ? [id] : [])
      } else {
        this.onReassign(id)
      }

      toastStore.remove(nanoId)
    }, 5000)

    const isPartialBulk =
      this._isBulk && !this.canReassignConversations && this.selectedHasAssignedConversation

    const title = this._isBulk
      ? isPartialBulk
        ? 'Assignment partially complete'
        : 'Assignment complete'
      : id
      ? `Conversation assigned to ${member?.name}`
      : 'Conversation unassigned'

    toastStore.add({
      id: nanoId,
      title,
      type: 'info',
      action: {
        text: 'Undo',
        onAction: () => {
          clearTimeout(assignTimeoutId)
          toastStore.remove(nanoId)
        },
      },
    })
  }

  reactionConversationId = () => {
    reaction(
      () => this._conversationId,
      (value) => {
        if (!value) return
        this.refresh(value)
      }
    )
  }

  get activeUserId() {
    return this._activeUserId
  }

  get search() {
    return this._search
  }

  get conversationAssignModalStore() {
    return this._conversationAssignModalStore
  }
}
